/**
 * Service Codes Settings Tab
 * Admin-only tab for managing master service codes with units/hour and cost per hour
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Edit,
  Trash2,
  DollarSign,
  Clock,
  Code,
  RefreshCw,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYER_TYPES = [
  { value: "none", label: "None" },
  { value: "medicaid", label: "Medicaid" },
  { value: "medicare", label: "Medicare" },
  { value: "private_pay", label: "Private Pay" },
  { value: "insurance", label: "Insurance" },
  { value: "va", label: "VA Benefits" },
  { value: "waiver", label: "Waiver Program" },
];

export function ServiceCodesSettingsTab() {
  const [serviceCodes, setServiceCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  const [formData, setFormData] = useState({
    code: "",
    modifier: "",
    description: "",
    units_per_hour: 1,
    cost_per_hour: 0,
    reimbursement_amount: 0,
    default_payer_type: "none",
    is_active: true,
  });

  const fetchServiceCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master-service-codes?include_inactive=${showInactive}`);
      setServiceCodes(response.data);
    } catch (error) {
      console.error("Failed to fetch service codes:", error);
      toast.error("Failed to load service codes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceCodes();
  }, [showInactive]);

  const resetForm = () => {
    setFormData({
      code: "",
      modifier: "",
      description: "",
      units_per_hour: 1,
      cost_per_hour: 0,
      reimbursement_amount: 0,
      default_payer_type: "none",
      is_active: true,
    });
    setEditingId(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleOpenEdit = (code) => {
    setEditingId(code.id);
    setFormData({
      code: code.code,
      modifier: code.modifier || "",
      description: code.description,
      units_per_hour: code.units_per_hour || 1,
      cost_per_hour: code.cost_per_hour || 0,
      reimbursement_amount: code.reimbursement_amount || 0,
      default_payer_type: code.default_payer_type || "none",
      is_active: code.is_active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.code || !formData.description) {
      toast.error("Code and description are required");
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        default_payer_type: formData.default_payer_type === "none" ? null : formData.default_payer_type,
      };
      
      if (editingId) {
        await axios.patch(`${API}/master-service-codes/${editingId}`, dataToSave);
        toast.success("Service code updated");
      } else {
        await axios.post(`${API}/master-service-codes`, dataToSave);
        toast.success("Service code created");
      }
      setDialogOpen(false);
      resetForm();
      fetchServiceCodes();
    } catch (error) {
      console.error("Failed to save service code:", error);
      toast.error(error.response?.data?.detail || "Failed to save service code");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (codeId) => {
    try {
      await axios.delete(`${API}/master-service-codes/${codeId}`);
      toast.success("Service code deleted");
      fetchServiceCodes();
    } catch (error) {
      console.error("Failed to delete service code:", error);
      toast.error("Failed to delete service code");
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await axios.post(`${API}/master-service-codes/seed-defaults`);
      toast.success(response.data.message);
      fetchServiceCodes();
    } catch (error) {
      console.error("Failed to seed defaults:", error);
      toast.error("Failed to seed default codes");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  return (
    <div className="space-y-6" data-testid="service-codes-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Master Service Codes</h2>
          <p className="text-sm text-slate-500">
            Configure service codes with default units per hour and hourly rates. These values auto-populate when creating authorizations.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              id="show-inactive"
            />
            <Label htmlFor="show-inactive" className="text-sm text-slate-600">
              Show Inactive
            </Label>
          </div>
          <Button
            variant="outline"
            onClick={handleSeedDefaults}
            className="gap-2"
            data-testid="seed-defaults-btn"
          >
            <Download className="w-4 h-4" />
            Load HCPCS Defaults
          </Button>
          <Button
            onClick={handleOpenAdd}
            className="gap-2 bg-teal-600 hover:bg-teal-700"
            data-testid="add-service-code-btn"
          >
            <Plus className="w-4 h-4" />
            Add Service Code
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="w-[90px]">Code</TableHead>
              <TableHead className="w-[60px]">Modifier</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="w-[80px] text-center">Units/Hr</TableHead>
              <TableHead className="w-[90px] text-right">Cost/Hr</TableHead>
              <TableHead className="w-[100px] text-right">Reimburse</TableHead>
              <TableHead className="w-[100px]">Payer</TableHead>
              <TableHead className="w-[65px] text-center">Status</TableHead>
              <TableHead className="w-[80px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                  Loading service codes...
                </TableCell>
              </TableRow>
            ) : serviceCodes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                  <Code className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No service codes configured</p>
                  <p className="text-xs mt-1">Click &quot;Load HCPCS Defaults&quot; to add common codes or &quot;Add Service Code&quot; to create custom ones.</p>
                </TableCell>
              </TableRow>
            ) : (
              serviceCodes.map((code) => (
                <TableRow
                  key={code.id}
                  className={!code.is_active ? "opacity-50 bg-slate-50" : ""}
                  data-testid={`service-code-row-${code.id}`}
                >
                  <TableCell className="font-mono font-semibold text-slate-800">
                    {code.code}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-blue-600">
                    {code.modifier || "-"}
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{code.description}</TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      {code.units_per_hour}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-medium text-red-600">
                    {formatCurrency(code.cost_per_hour)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-emerald-700">
                    {formatCurrency(code.reimbursement_amount)}
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs">
                    {PAYER_TYPES.find(p => p.value === code.default_payer_type)?.label || "-"}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                      code.is_active 
                        ? "bg-emerald-100 text-emerald-700" 
                        : "bg-slate-100 text-slate-600"
                    }`}>
                      {code.is_active ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenEdit(code)}
                        data-testid={`edit-code-${code.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-code-${code.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Service Code?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete service code &quot;{code.code}&quot;? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(code.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setDialogOpen(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-teal-600" />
              {editingId ? "Edit Service Code" : "Add Service Code"}
            </DialogTitle>
            <DialogDescription>
              Configure service code with default rates for authorizations
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Service Code *</Label>
                <Input
                  id="code"
                  placeholder="e.g., T1019"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="font-mono"
                  data-testid="service-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="modifier">Modifier</Label>
                <Input
                  id="modifier"
                  placeholder="e.g., U1, U2"
                  value={formData.modifier}
                  onChange={(e) => setFormData({ ...formData, modifier: e.target.value.toUpperCase() })}
                  className="font-mono"
                  maxLength={4}
                  data-testid="service-modifier-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    id="status"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <span className="text-sm text-slate-600">
                    {formData.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                placeholder="e.g., Personal Care Services"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                data-testid="service-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="units_per_hour" className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  Units per Hour
                </Label>
                <Input
                  id="units_per_hour"
                  type="number"
                  step="0.25"
                  min="0"
                  value={formData.units_per_hour}
                  onChange={(e) => setFormData({ ...formData, units_per_hour: parseFloat(e.target.value) || 0 })}
                  data-testid="units-per-hour-input"
                />
                <p className="text-xs text-slate-500">Standard is 4 units = 1 hour</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost_per_hour" className="flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5 text-red-500" />
                  Cost per Hour
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="cost_per_hour"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost_per_hour}
                    onChange={(e) => setFormData({ ...formData, cost_per_hour: parseFloat(e.target.value) || 0 })}
                    className="pl-7"
                    data-testid="cost-per-hour-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Amount paid to caregiver</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reimbursement_amount" className="flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                  Reimbursement Amount
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="reimbursement_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.reimbursement_amount}
                    onChange={(e) => setFormData({ ...formData, reimbursement_amount: parseFloat(e.target.value) || 0 })}
                    className="pl-7"
                    data-testid="reimbursement-amount-input"
                  />
                </div>
                <p className="text-xs text-slate-500">Amount billed to payer</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_payer">Default Payer Type</Label>
                <Select
                  value={formData.default_payer_type}
                  onValueChange={(value) => setFormData({ ...formData, default_payer_type: value })}
                >
                  <SelectTrigger data-testid="default-payer-select">
                    <SelectValue placeholder="Select default payer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYER_TYPES.map((payer) => (
                      <SelectItem key={payer.value} value={payer.value}>
                        {payer.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="save-service-code-btn"
            >
              {saving ? "Saving..." : "Save Service Code"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
